/**
 * Catálogo declarativo de features del producto.
 *
 * Es el origen único de verdad para:
 *  - el megamenu de la Navbar
 *  - la página índice /features
 *  - cada página dedicada /features/:slug
 *  - la inyección SEO server-side
 *  - el sitemap.xml
 *
 * Para añadir una feature nueva basta con sumar un objeto a `FEATURES`:
 * automáticamente aparece en el menú, en el índice y se le genera una página
 * con SEO propio. El copy se puede iterar después; el campo `sections` puede
 * dejarse vacío en la primera versión (la plantilla lo maneja).
 */
import type { ComponentType } from 'react';
import {
  CalendarDays,
  Wand2,
  Lightbulb,
  Sparkles,
  BarChart3,
  AtSign,
  Code,
  Layers,
  Palette,
  Clock,
  Languages,
  TrendingUp,
  Image as ImageIcon,
  UserCheck,
  Bot,
  Users,
  type LucideIcon,
} from 'lucide-react';
import {
  CalendarFeatureMockup,
  QueueFeatureMockup,
  AiFeatureMockup,
  PerformanceMockup,
  FormatsFeatureMockup,
  PreviewsFeatureMockup,
  BrandFeatureMockup,
  LinkInBioFeatureMockup,
  ApiFeatureMockup,
  PerNetworkCaptionFeatureMockup,
  TimezoneFeatureMockup,
  AnalyticsFeatureMockup,
  MultiNetworkMockup,
  ApprovalFeatureMockup,
  McpFeatureMockup,
  TeamFeatureMockup,
} from '../pages/Landing';

export type FeatureCategoryId = 'publicacion' | 'ia' | 'analisis' | 'plataforma' | 'devs';

export type FeatureCategory = {
  id: FeatureCategoryId;
  label: string;
  short: string;
};

export const FEATURE_CATEGORIES: FeatureCategory[] = [
  { id: 'publicacion', label: 'Publicación',  short: 'Programa y publica' },
  { id: 'ia',          label: 'IA y contenido', short: 'Genera con IA' },
  { id: 'analisis',    label: 'Análisis',      short: 'Mide y mejora' },
  { id: 'plataforma',  label: 'Plataforma',    short: 'Para tu marca' },
  { id: 'devs',        label: 'Desarrolladores', short: 'API y automatización' },
];

export type FeatureBenefit = { title: string; body: string };
export type FeatureFaq = { q: string; a: string };
export type FeatureStep = { title: string; body: string };

export type Feature = {
  /** URL-safe identifier, used as `/features/:slug`. */
  slug: string;
  category: FeatureCategoryId;
  /** Small text above the title (in cards and on the hero). */
  eyebrow: string;
  /** Main name, used in nav + hero + page title. */
  title: string;
  /** One-liner shown in the megamenu under the title. */
  shortDescription: string;
  /** Two-three-sentence pitch for the hero + meta description. */
  description: string;
  icon: LucideIcon;
  /** Decorative animated mockup component shown in the hero. */
  Mockup?: ComponentType;
  /** Optional badge like NUEVO / BETA. */
  badge?: 'NUEVO' | 'BETA' | 'PRO';
  /** Where the primary CTA leads (typically /register or /dashboard/...). */
  cta?: { label: string; href: string };
  /** Section bodies are optional — the page template gracefully skips empties. */
  sections?: {
    problem?: { title: string; body: string };
    howItWorks?: { title: string; steps: FeatureStep[] };
    benefits?: { title: string; items: FeatureBenefit[] };
    faq?: FeatureFaq[];
  };
  /** Slugs of related features rendered at the bottom of the page. */
  relatedSlugs?: string[];
  /** Custom SEO; falls back to `description` if missing. */
  seo?: {
    title?: string;
    description?: string;
    keywords?: string;
  };
};

/**
 * The actual catalog. Copy lives here so non-engineers can iterate it in a
 * single file without diving into JSX. Sections marked TODO are placeholder —
 * fill them in feature-by-feature in Fase 2.
 */
export const FEATURES: Feature[] = [
  /* ── Publicación ──────────────────────────────────────── */
  {
    slug: 'programar-publicaciones',
    category: 'publicacion',
    eyebrow: 'Programación',
    title: 'Programa una vez, publica en todas',
    shortDescription: 'Calendario visual, drag & drop, cola inteligente.',
    description:
      'Carga tus publicaciones cuando tengas tiempo y deja que Zettapost las publique cuando tu audiencia esté online. Calendario visual con arrastrar y soltar, vista mes y semana, y cola con tus mejores huecos.',
    icon: CalendarDays,
    Mockup: CalendarFeatureMockup,
    cta: { label: 'Empieza a programar', href: '/register' },
    relatedSlugs: ['cola-publicacion', 'multi-red', 'texto-por-red'],
  },
  {
    slug: 'cola-publicacion',
    category: 'publicacion',
    eyebrow: 'Cola inteligente',
    title: 'Mejores horas, sin pensar',
    shortDescription: 'Slots semanales recurrentes que se rellenan solos.',
    description:
      'Define una vez tus mejores franjas (por ejemplo: lunes a viernes 9:00, 13:00 y 19:00). Después arrastra cualquier post a la cola y se publicará en el siguiente hueco libre, manteniendo una cadencia constante.',
    icon: Clock,
    Mockup: QueueFeatureMockup,
    cta: { label: 'Configura tu cola', href: '/register' },
    relatedSlugs: ['programar-publicaciones', 'zona-horaria'],
  },
  {
    slug: 'multi-red',
    category: 'publicacion',
    eyebrow: 'Multi-red',
    title: 'Todas tus redes en un solo lugar',
    shortDescription: 'Zettapost es compatible con 9 Redes sociales diferentes',
    description:
      'Instagram, Facebook, X, LinkedIn, TikTok, YouTube, Threads, Pinterest y Bluesky — todas conectadas con OAuth oficial. Un solo composer, una sola programación, todas las redes.',
    icon: Layers,
    Mockup: MultiNetworkMockup,
    cta: { label: 'Conecta tus cuentas', href: '/register' },
    relatedSlugs: ['texto-por-red', 'formatos'],
  },
  {
    slug: 'texto-por-red',
    category: 'publicacion',
    eyebrow: 'Texto por red',
    title: 'Un post, diferentes captions',
    shortDescription: 'Personaliza el caption de tu post por red social de manera sencilla.',
    description:
      'Escribe el texto general una vez y personalízalo por red social cuando quieras: emojis y hashtags para Instagram, breve para X, profesional para LinkedIn. Si dejas el override vacío vuelve al texto general automáticamente.',
    icon: Languages,
    Mockup: PerNetworkCaptionFeatureMockup,
    badge: 'NUEVO',
    cta: { label: 'Pruébalo ahora', href: '/register' },
    relatedSlugs: ['multi-red', 'ia-captions'],
  },
  {
    slug: 'formatos',
    category: 'publicacion',
    eyebrow: 'Formatos',
    title: 'Posts, Reels, Stories, Shorts y Pins',
    shortDescription: 'Cada red con sus formatos nativos.',
    description:
      'No estás limitado a "una imagen y un texto": en cada red puedes elegir el formato adecuado — Reel y Story en Instagram, Short en YouTube, Pin en Pinterest — todo desde el mismo editor.',
    icon: ImageIcon,
    Mockup: FormatsFeatureMockup,
    cta: { label: 'Crea tu primer post', href: '/register' },
    relatedSlugs: ['previews', 'multi-red'],
  },
  {
    slug: 'previews',
    category: 'publicacion',
    eyebrow: 'Previews',
    title: 'Mira cómo queda antes de publicar',
    shortDescription: 'Previsualización fiel red por red.',
    description:
      'Cada red recorta, formatea y muestra los posts de forma distinta. Antes de publicar te enseñamos cómo se verá en cada una para que no te lleves sorpresas — incluyendo dimensiones, recortes y truncado de copy.',
    icon: ImageIcon,
    Mockup: PreviewsFeatureMockup,
    cta: { label: 'Probar gratis', href: '/register' },
    relatedSlugs: ['formatos', 'multi-red'],
  },
  {
    slug: 'zona-horaria',
    category: 'publicacion',
    eyebrow: 'Zona horaria',
    title: 'Publicado a la hora correcta',
    shortDescription: 'Tu zona horaria, sin sorpresas con los cambios de hora.',
    description:
      'Todas tus publicaciones se programan en tu zona horaria local, con soporte automático para cambios de horario (DST). Editable en cualquier momento desde tu perfil.',
    icon: Clock,
    Mockup: TimezoneFeatureMockup,
    cta: { label: 'Probar gratis', href: '/register' },
    relatedSlugs: ['programar-publicaciones', 'cola-publicacion'],
  },

  /* ── IA & Contenido ───────────────────────────────────── */
  {
    slug: 'ia-captions',
    category: 'ia',
    eyebrow: 'IA · Captions',
    title: 'Captions perfectos a partir de tu imagen',
    shortDescription: 'Sube tu media, la IA escribe el copy adaptado a la red.',
    description:
      'Sube una imagen o vídeo y nuestra IA escribe un caption adaptado a la red social elegida, con hashtags incluidos. Ideal cuando tienes el contenido pero no la inspiración para el texto.',
    icon: Wand2,
    Mockup: AiFeatureMockup,
    cta: { label: 'Probar gratis', href: '/register' },
    relatedSlugs: ['ia-ideas', 'texto-por-red', 'inspiracion'],
  },
  {
    slug: 'ia-ideas',
    category: 'ia',
    eyebrow: 'IA · Ideas',
    title: '10 ideas listas, en menos de 30 segundos',
    shortDescription: 'IA que sugiere ángulos, hooks y hashtags por red.',
    description:
      'Si no sabes qué publicar, dile a la IA tu tema (o ningún tema) y te devolverá 10 ideas adaptadas a tu marca y a la red elegida. Cada idea incluye ángulo, hook, ejemplo de copy y hashtags.',
    icon: Lightbulb,
    Mockup: AiFeatureMockup,
    cta: { label: 'Generar ideas', href: '/register' },
    relatedSlugs: ['ia-captions', 'inspiracion'],
  },
  {
    slug: 'prediccion',
    category: 'ia',
    eyebrow: 'IA · Predicción',
    title: 'Predicción de rendimiento',
    shortDescription: 'Antes de publicar, nuestra IA puntúa tu post y te sugiere mejoras concretas. Para que publiques con toda la confianza de un equipo de marketing autónomo.',
    description:
      'Antes de publicar, la IA analiza tu caption y tus imágenes y te da una puntuación de 0 a 100 con mejoras concretas: gancho, claridad, longitud, hashtags, llamada a la acción. Publica con confianza.',
    icon: TrendingUp,
    Mockup: PerformanceMockup,
    cta: { label: 'Probar gratis', href: '/register' },
    relatedSlugs: ['ia-captions', 'analytics'],
  },
  {
    slug: 'inspiracion',
    category: 'ia',
    eyebrow: 'Inspiración diaria',
    title: 'Una plantilla nueva cada día, gratis',
    shortDescription: 'Galería de plantillas por nicho generadas con IA.',
    description:
      'Cada día generamos una plantilla por nicho (restaurantes, ecommerce, coaches, belleza, fitness, inmobiliaria) con imagen y caption listos. Cópialos en un clic a tu composer para publicar.',
    icon: Sparkles,
    Mockup: AiFeatureMockup,
    cta: { label: 'Ver galería', href: '/register' },
    relatedSlugs: ['ia-ideas', 'ia-captions'],
  },

  /* ── Análisis ─────────────────────────────────────────── */
  {
    slug: 'analytics',
    category: 'analisis',
    eyebrow: 'Analytics',
    title: 'Métricas unificadas de todas tus redes',
    shortDescription: 'Analiza el rendimiento de tus posts en todas las redes conectadas',
    description:
      'Analiza el rendimiento de todas tus cuentas conectadas desde un único panel. Filtra por red, por cuenta o por periodo, y exporta los datos para compartir informes con tu equipo o clientes.',
    icon: BarChart3,
    Mockup: AnalyticsFeatureMockup,
    cta: { label: 'Probar gratis', href: '/register' },
    relatedSlugs: ['prediccion'],
  },

  /* ── Plataforma ───────────────────────────────────────── */
  {
    slug: 'link-in-bio',
    category: 'plataforma',
    eyebrow: 'Link in bio',
    title: 'Link in bio',
    shortDescription: 'Una página para todos tus enlaces totalmente personalizada y con captura de email gratis',
    description:
      'Crea una página de enlaces en tu dominio zlink.club/tu-marca. Personaliza colores y fondo, añade un banner destacado, conecta Mailchimp para capturar emails y mide vistas y clics desde el dashboard.',
    icon: AtSign,
    Mockup: LinkInBioFeatureMockup,
    badge: 'NUEVO',
    cta: { label: 'Crear mi link in bio', href: '/register' },
    relatedSlugs: ['marca', 'analytics'],
  },
  {
    slug: 'marca',
    category: 'plataforma',
    eyebrow: 'Brand kit',
    title: 'Tu identidad de marca, lista para usar',
    shortDescription: 'Logo, paleta y descripción reutilizables en todo el producto.',
    description:
      'Guarda tu logo, paleta de colores y descripción de marca una sola vez. Después se usan automáticamente en tu Link in Bio, en los previews y como contexto para que la IA escriba como tú.',
    icon: Palette,
    Mockup: BrandFeatureMockup,
    cta: { label: 'Configurar marca', href: '/register' },
    relatedSlugs: ['link-in-bio', 'ia-captions'],
  },
  {
    slug: 'aprobacion-cliente',
    category: 'plataforma',
    eyebrow: 'Aprobación de cliente',
    title: 'Aprobación antes de publicar',
    shortDescription: 'Comparte un enlace privado para que tu cliente revise, apruebe o pida cambios para cada post',
    description:
      'Envía a tu cliente un enlace privado donde revisa cada publicación con su preview real y decide red por red: aprobar, pedir cambios o rechazar, con comentarios. Las redes aprobadas se publican solas; las que piden cambios quedan a la espera. Vincula su email y la solicitud le llega automáticamente. Pensado para agencias y community managers.',
    icon: UserCheck,
    Mockup: ApprovalFeatureMockup,
    badge: 'NUEVO',
    cta: { label: 'Probar el flujo de aprobación', href: '/register' },
    sections: {
      problem: {
        title: 'Se acabó el ida y vuelta por WhatsApp y email',
        body: 'Mandar capturas, esperar el "ok", perder comentarios en mil chats y no saber qué quedó aprobado. Con un enlace de revisión tu cliente lo ve tal cual saldrá y tú tienes todo el historial en un sitio.',
      },
      howItWorks: {
        title: 'Cómo funciona',
        steps: [
          { title: 'Crea la publicación y pulsa "Pedir aprobación"', body: 'En vez de publicar, generas un enlace privado de revisión. La publicación queda retenida, fuera de la cola, hasta que se apruebe.' },
          { title: 'Tu cliente revisa con el preview real', body: 'Abre el enlace (sin cuenta) y ve cómo quedará en cada red. Decide red por red: aprobar, pedir cambios o rechazar, con comentarios.' },
          { title: 'Se publica solo lo aprobado', body: 'Cada red aprobada se publica automáticamente a su hora. Las que piden cambios esperan a que las edites y reenvíes.' },
        ],
      },
      benefits: {
        title: 'Por qué te encantará',
        items: [
          { title: 'Aprobación por red', body: 'Tu cliente puede aprobar Instagram y pedir cambios en LinkedIn en la misma publicación.' },
          { title: 'Preview real', body: 'Revisa el mockup fiel de cada red, no un texto plano: carrusel, recortes y caption como se verá.' },
          { title: 'Email automático a revisores', body: 'Vincula uno o varios emails a cada cuenta y la solicitud les llega sola al pedir aprobación.' },
          { title: 'Todo el historial', body: 'Quién aprobó, qué pidió cambiar y cuándo — registrado en el panel de Aprobaciones.' },
        ],
      },
      faq: [
        { q: '¿El cliente necesita una cuenta de Zettapost?', a: 'No. Revisa y decide desde el enlace privado, sin registrarse.' },
        { q: '¿Puede aprobar unas redes y rechazar otras?', a: 'Sí. La decisión es por red: las aprobadas se publican y las rechazadas no, en la misma publicación.' },
        { q: '¿Le llega el enlace automáticamente?', a: 'Si vinculas su email a la cuenta en el panel de Aprobaciones, recibe la solicitud por email en cuanto pulsas "Pedir aprobación". También puedes copiar y compartir el enlace tú.' },
      ],
    },
    relatedSlugs: ['previews', 'multi-red', 'marca'],
  },
  {
    slug: 'equipos',
    category: 'plataforma',
    eyebrow: 'Equipos',
    title: 'Trabaja en equipo',
    shortDescription: 'Invita a tu equipo, asígnales cuentas y gestionad las redes juntos.',
    description:
      'Invita a tus compañeros por email o enlace y trabajad las redes desde un mismo espacio. Tú, como dueño, decides a qué secciones y a qué cuentas accede cada miembro. Pensado para agencias, equipos de marketing y marcas con varias manos.',
    icon: Users,
    Mockup: TeamFeatureMockup,
    badge: 'NUEVO',
    cta: { label: 'Crear mi equipo', href: '/register' },
    sections: {
      problem: {
        title: 'Se acabó compartir la contraseña',
        body: 'Pasar credenciales por WhatsApp, no saber quién publicó qué, o que un freelance toque cuentas que no debería. Con equipos cada persona entra con su propia cuenta y solo ve lo que le asignas.',
      },
      howItWorks: {
        title: 'Cómo funciona',
        steps: [
          { title: 'Invita a tu equipo', body: 'Por email o con un enlace de invitación. Tus compañeros crean su cuenta y entran directamente en tu espacio de trabajo.' },
          { title: 'Asigna cuentas y permisos', body: 'Decides a qué secciones (publicar, calendario, analíticas…) y a qué cuentas concretas puede acceder cada miembro.' },
          { title: 'Trabajad juntos', body: 'Todos programan y publican desde el mismo sitio. La facturación, la gestión del equipo y conectar/eliminar cuentas quedan solo para ti, el dueño.' },
        ],
      },
      benefits: {
        title: 'Por qué te encantará',
        items: [
          { title: 'Cada uno con su acceso', body: 'Sin compartir contraseñas: cada miembro entra con su propia cuenta.' },
          { title: 'Permisos por sección y por cuenta', body: 'Controlas exactamente qué ve y qué puede tocar cada persona.' },
          { title: 'Un solo espacio de trabajo', body: 'Todo el equipo gestiona las mismas redes desde el espacio del dueño.' },
          { title: 'Para agencias y marcas', body: 'Suma asientos según tu plan y escala tu operación sin fricción.' },
        ],
      },
      faq: [
        { q: '¿Mis compañeros necesitan su propia cuenta?', a: 'Sí, cada miembro crea su cuenta y opera dentro de tu espacio de trabajo. Sin compartir contraseñas.' },
        { q: '¿Puedo limitar a qué cuentas accede cada uno?', a: 'Sí. Los permisos son por sección y por cuenta: asignas a cada miembro exactamente lo que puede ver y gestionar.' },
        { q: '¿Quién puede gestionar la facturación y conectar cuentas?', a: 'Solo el dueño del espacio. Los miembros publican y gestionan según sus permisos, pero la facturación, la gestión del equipo y conectar/eliminar cuentas son exclusivas del dueño.' },
        { q: '¿Cuántas personas puedo invitar?', a: 'Depende de tu plan: el plan Business incluye asientos para tu equipo y los planes superiores amplían el límite.' },
      ],
    },
    relatedSlugs: ['aprobacion-cliente', 'marca', 'multi-red'],
  },

  /* ── Desarrolladores ──────────────────────────────────── */
  {
    slug: 'api',
    category: 'devs',
    eyebrow: 'API REST',
    title: 'API Disponible',
    shortDescription: 'Publica y programa desde tu código o IA con nuestra API',
    description:
      'API REST con autenticación Bearer y rate limit generoso. Lista tus cuentas conectadas, crea publicaciones (con media por URL pública) y consulta el estado por target. Documentación OpenAPI y ejemplos curl listos para copiar.',
    icon: Code,
    Mockup: ApiFeatureMockup,
    badge: 'NUEVO',
    cta: { label: 'Ver documentación', href: '/dashboard/api' },
    relatedSlugs: ['texto-por-red', 'programar-publicaciones', 'mcp-chatgpt'],
  },
  {
    slug: 'mcp-chatgpt',
    category: 'devs',
    eyebrow: 'ChatGPT · MCP',
    title: 'Gestiona tus redes desde ChatGPT',
    shortDescription: 'Conecta Zettapost a ChatGPT, Claude o Cursor y publica hablando con la IA.',
    description:
      'Conecta Zettapost a ChatGPT, Claude o Cursor con nuestro conector MCP y gestiona tus redes hablando con la IA: crea, programa, edita y cancela publicaciones, sube media y consulta analíticas. Sin pegar tokens — autenticación OAuth segura y separada de tu API key.',
    icon: Bot,
    Mockup: McpFeatureMockup,
    badge: 'NUEVO',
    cta: { label: 'Conectar con ChatGPT', href: '/chatgpt' },
    sections: {
      problem: {
        title: 'Tu IA ya escribe el contenido. Que también lo publique.',
        body: 'Pides a ChatGPT que te redacte un post, lo copias, abres otra herramienta, lo pegas, eliges redes y hora… Con el conector MCP, la IA hace todo eso por ti dentro de la misma conversación.',
      },
      howItWorks: {
        title: 'Cómo funciona',
        steps: [
          { title: 'Añade el conector', body: 'En ChatGPT → Conectores → pega https://zettapost.com/mcp y elige OAuth. Se registra solo (DCR): no creas ni pegas ningún token.' },
          { title: 'Inicia sesión y autoriza', body: 'Inicias sesión en Zettapost y pulsas "Autorizar". El acceso es OAuth, independiente de tu token de API.' },
          { title: 'Pídeselo en lenguaje natural', body: '"Publica esto en Instagram y X mañana a las 10", "¿cómo fueron mis redes este mes?", "cancela el post del viernes". La IA lo ejecuta.' },
        ],
      },
      benefits: {
        title: 'Por qué te encantará',
        items: [
          { title: 'Sin copiar y pegar', body: 'Crear, programar, editar y cancelar publicaciones sin salir del chat.' },
          { title: 'Login seguro con OAuth', body: 'El conector usa OAuth, separado de tu API key. Puedes revocar el acceso cuando quieras.' },
          { title: 'Funciona en varios clientes', body: 'ChatGPT, Claude, Cursor y cualquier cliente compatible con MCP.' },
          { title: 'Analíticas y cuentas', body: 'Consulta métricas, mejores publicaciones y tus cuentas conectadas preguntando.' },
        ],
      },
      faq: [
        { q: '¿Necesito saber programar?', a: 'No. Solo pegas la URL del conector en ChatGPT, inicias sesión y listo. Hablas con la IA en lenguaje natural.' },
        { q: '¿Es seguro? ¿Usa mi token de API?', a: 'El conector usa OAuth, una autenticación independiente de tu token de API. Autorizas el acceso desde Zettapost y puedes revocarlo en cualquier momento.' },
        { q: '¿Puede ChatGPT subir una imagen que genera?', a: 'La media se adjunta por URL pública (o subiéndola en bytes con upload_media). Muchos clientes no pueden enviar los bytes de una imagen generada, así que en ese caso necesitarás una URL pública.' },
        { q: '¿En qué planes está incluido?', a: 'En todos los planes activos, igual que la API.' },
      ],
    },
    seo: {
      title: 'Conecta Zettapost con ChatGPT (MCP) · Publica tus redes desde la IA',
      description:
        'Conector MCP para gestionar tus redes desde ChatGPT, Claude o Cursor: publica, programa, edita y mide hablando con la IA. Login OAuth, sin pegar tokens.',
      keywords: 'MCP, ChatGPT, conector, publicar redes desde ChatGPT, Model Context Protocol, Claude, Cursor',
    },
    relatedSlugs: ['api', 'programar-publicaciones', 'analytics'],
  },
];

export function getFeatureBySlug(slug: string): Feature | undefined {
  return FEATURES.find((f) => f.slug === slug);
}

export function getFeaturesByCategory(): { category: FeatureCategory; features: Feature[] }[] {
  return FEATURE_CATEGORIES.map((category) => ({
    category,
    features: FEATURES.filter((f) => f.category === category.id),
  }));
}
